import { get } from '#layers/base/public';

export const blockExpressionMarker = '$:';

export interface BlockExpressionASTStatic {
	type: 'static';
	value: any;
}

export interface BlockExpressionASTExpression {
	type: 'expression';
	child: BlockExpressionChildAST;
}

export interface BlockExpressionASTProp {
	type: 'prop';
	path: string[];
}
export type BlockExpressionChildAST = BlockExpressionASTStatic | BlockExpressionASTProp;

export type BlockExpressionRootAST = BlockExpressionASTExpression | BlockExpressionASTStatic;

export type BlockParsedExpression = Array<BlockExpressionRootAST>;

function parseExpression(value: string): BlockExpressionASTExpression {
	return {
		type: 'expression',
		child: parseExpressionChild(value),
	};
}

function parseExpressionChild(value: string): BlockExpressionChildAST {
	value = value.trim();

	if (value === 'true') {
		return { type: 'static', value: true };
	}

	if (value === 'false') {
		return { type: 'static', value: false };
	}

	if (value === 'null') {
		return { type: 'static', value: null };
	}

	if (value === 'undefined') {
		return { type: 'static', value: undefined };
	}

	if (/^[\d.]+$/.test(value)) {
		return { type: 'static', value: Number(value) };
	}

	if (/^['"`]/.test(value)) {
		if (value.at(0) !== value.at(-1)) {
			throw new Error(`Unterminated quote in string: ${value}`);
		}

		return { type: 'static', value: value.slice(1, -1) };
	}

	return { type: 'prop', path: value.split('.').map((part) => part.trim()).filter(Boolean) };
}

export function parseBlockExpression(expression: string): BlockParsedExpression {
	const ast: Array<BlockExpressionRootAST> = [];
	let startIndex: number;
	let endIndex = 0;

	while ((startIndex = expression.indexOf('{{', endIndex)) !== -1) {
		if (startIndex > endIndex) {
			ast.push({
				type: 'static',
				value: expression.slice(endIndex, startIndex),
			});
		}

		endIndex = expression.indexOf('}}', startIndex + 2);

		if (endIndex === -1) {
			throw new Error(`Unmatched '}}' in expression: ${expression}`);
		}

		const expr = expression.slice(startIndex + 2, endIndex).trim();
		ast.push(parseExpression(expr));
		endIndex += 2;
	}

	if (endIndex < expression.length) {
		ast.push({
			type: 'static',
			value: expression.slice(Math.max(0, endIndex)),
		});
	}

	return ast;
}

export interface UseBlockExpressionEvalOptions {
	/**
	 * Variable values (name => value).
	 */
	variables: Record<string, any>;
	/**
	 * Variable id to name mapping.
	 */
	variableIdToName?: Map<string, string>;
	/**
	 * Additional API to use in expressions.
	 * For example: `{ $route: useRoute() }`
	 */
	additionalApi?: Record<string, any>;
}

export function useBlockExpressionEval(options: UseBlockExpressionEvalOptions) {
	const {
		variables,
		variableIdToName = new Map(),
		additionalApi = {},
	} = options;

	function compileAstExpression(expression: BlockExpressionASTExpression) {
		return compileChildAst(expression.child);
	}

	function compileChildAst(child: BlockExpressionChildAST): () => any {
		if (child.type === 'static') {
			return () => child.value;
		}

		if (child.type === 'prop') {
			return compileAstProp(child);
		}

		throw new Error(`Unknown AST node type: ${child}`);
	}

	function compileAstProp(prop: BlockExpressionASTProp) {
		// Allow access to properties using '.' notation
		const pathFirstPart = prop.path[0];
		// Variable uuid
		// For example: {{ 2ee28825-b1a6-46e4-8306-ee1e0f0b1297 }}
		const variableName = pathFirstPart && variableIdToName.get(pathFirstPart);

		if (variableName) {
			// Use variable name instead of variable id
			prop.path[0] = variableName;
		}

		const path = prop.path.join('.');

		return () => get({
			...variables,
			...additionalApi,
		}, path);
	}

	function compileExpression(expression: string) {
		const ast = parseBlockExpression(expression);
		const parts: Array<any | (() => any)> = [];

		for (const astNode of ast) {
			if (astNode.type === 'static') {
				parts.push(astNode.value);
			}
			else if (astNode.type === 'expression') {
				parts.push(compileAstExpression(astNode));
			}
		}

		return () => {
			const result = parts.map((part) => typeof part === 'function' ? part() : part);

			if (result.length === 1) {
				return result[0];
			}

			return result.join('');
		};
	}

	return {
		compileExpression,
	};
}
